/**
 * Expression Evaluator for RUL Forms
 *
 * Evaluates conditional expressions like:
 * - "field_name == value"
 * - "field_name != value"
 * - "field_name == true"
 * - "field_name != null"
 */

/**
 * Evaluate a conditional expression against form data
 *
 * @param expression - The expression string (e.g., "different_arrival == true")
 * @param formData - The form data object with field values
 * @returns boolean - Whether the expression evaluates to true
 */
export function evaluateExpression(
  expression: string | undefined | null,
  formData: Record<string, any>
): boolean {
  // No expression means always true (visible/enabled by default)
  if (!expression) {
    return true;
  }

  const trimmedExpr = expression.trim();

  // Parse the expression: "field_name operator value"
  // Supports: ==, !=
  const eqMatch = trimmedExpr.match(/^(\w+)\s*==\s*(.+)$/);
  const neqMatch = trimmedExpr.match(/^(\w+)\s*!=\s*(.+)$/);

  if (eqMatch) {
    const [, fieldName, rawValue] = eqMatch;
    const fieldValue = getFieldValue(fieldName, formData);
    const compareValue = parseValue(rawValue.trim());
    return isEqual(fieldValue, compareValue);
  }

  if (neqMatch) {
    const [, fieldName, rawValue] = neqMatch;
    const fieldValue = getFieldValue(fieldName, formData);
    const compareValue = parseValue(rawValue.trim());
    return !isEqual(fieldValue, compareValue);
  }

  // If we can't parse the expression, default to true
  console.warn(`Unable to parse expression: "${expression}"`);
  return true;
}

/**
 * Get the value of a field from form data
 */
function getFieldValue(fieldName: string, formData: Record<string, any>): any {
  const field = formData[fieldName];
  if (!field) {
    return undefined;
  }
  // Field can be a widget object with value, or a direct value
  return field.value !== undefined ? field.value : field;
}

/**
 * Parse a value string into its actual type
 */
function parseValue(rawValue: string): any {
  // Boolean
  if (rawValue === 'true') return true;
  if (rawValue === 'false') return false;

  // Null
  if (rawValue === 'null') return null;

  // Number
  if (/^-?\d+(\.\d+)?$/.test(rawValue)) {
    return parseFloat(rawValue);
  }

  // String (with or without quotes)
  if (
    (rawValue.startsWith('"') && rawValue.endsWith('"')) ||
    (rawValue.startsWith("'") && rawValue.endsWith("'"))
  ) {
    return rawValue.slice(1, -1);
  }

  // Plain string
  return rawValue;
}

/**
 * Compare two values for equality
 */
function isEqual(a: any, b: any): boolean {
  // Handle null comparison
  if (b === null) {
    return a === null || a === undefined || a === '';
  }

  // Handle boolean comparison (also check string "true"/"false")
  if (typeof b === 'boolean') {
    if (typeof a === 'boolean') return a === b;
    if (typeof a === 'string') return (a.toLowerCase() === 'true') === b;
    return Boolean(a) === b;
  }

  // Strict equality for other types
  return a === b;
}
